import type {
  IBaseError,
  ErrorOptions,
  ErrorContext,
  ErrorCategory,
  ErrorSeverity,
} from "../types/index";

/**
 * Base error class that implements the BaseError interface
 *
 * This class serves as the foundation for all application errors,
 * providing standardized error handling with context, serialization,
 * and proper inheritance.
 */
export class BaseError extends Error implements IBaseError {
  public readonly category: ErrorCategory;
  public readonly severity: ErrorSeverity;
  public readonly statusCode?: number;
  public readonly context?: ErrorContext;
  public readonly reportable: boolean;
  public readonly userVisible: boolean;
  public readonly code: string;

  public readonly cause?: Error;

  constructor(options: ErrorOptions) {
    super(options.message);

    this.name = this.constructor.name;
    this.category = options.category;
    this.severity = options.severity ?? "medium";
    this.statusCode = options.statusCode;
    this.reportable = options.reportable ?? true;
    this.userVisible = options.userVisible ?? false;
    this.code = options.code;

    // Set up context with timestamp and stack
    this.context = {
      timestamp: new Date(),
      stack: this.stack,
      ...options.context,
    };

    // Set the cause if provided
    if (options.cause) {
      this.cause = options.cause;
    }

    // Ensure proper prototype chain
    Object.setPrototypeOf(this, new.target.prototype);
  }

  /**
   * Convert error to a plain object for serialization
   */
  toJSON(): Record<string, unknown> {
    const result: Record<string, unknown> = {
      name: this.name,
      message: this.message,
      category: this.category,
      severity: this.severity,
      statusCode: this.statusCode,
      code: this.code,
      context: this.context,
      reportable: this.reportable,
      userVisible: this.userVisible,
      stack: this.stack,
    };

    // Include cause information if present
    if (this.cause) {
      result.cause = {
        name: this.cause.name,
        message: this.cause.message,
        stack: this.cause.stack,
      };
    }

    return result;
  }

  /**
   * Convert error to a user-safe format (hides sensitive information)
   */
  toUserSafe(): Record<string, unknown> {
    return {
      message: this.userVisible ? this.message : "An error occurred",
      code: this.code,
      category: this.category,
      statusCode: this.statusCode,
      requestId: this.context?.requestId,
    };
  }
}
